import { __assign, __awaiter, __extends, __generator, __rest } from "tslib";
import React, { useEffect, Suspense, useState, useReducer, useRef } from 'react';
import { getCachedModule, handlePrefetchPromise, getAppData, processManifest, getPendingLoading, setPendingLoading, getPendingPrefetch, } from '@scalprum/core';
import isEqual from 'lodash/isEqual';
import { loadComponent } from './async-loader';
import DefaultErrorComponent from './default-error-component';
import { PrefetchProvider } from './prefetch-context';
import { useScalprum } from './use-scalprum';
function setComponentFromModule(scope, module, isMounted, importName, setComponent) {
    return __awaiter(this, void 0, void 0, function () {
        var _a, prefetch, component;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, loadComponent(scope, module, importName)];
                case 1:
                    _a = _b.sent(), prefetch = _a.prefetch, component = _a.component;
                    isMounted && setComponent(function () { return component; });
                    return [2 /*return*/, prefetch];
            }
        });
    });
}
var LoadModule = function (_a) {
    var _b = _a.fallback, fallback = _b === void 0 ? 'loading' : _b, scope = _a.scope, module = _a.module, ErrorComponent = _a.ErrorComponent, processor = _a.processor, innerRef = _a.innerRef, _c = _a.skipCache, skipCache = _c === void 0 ? false : _c, _d = _a.importName, importName = _d === void 0 ? 'default' : _d, props = __rest(_a, ["fallback", "scope", "module", "ErrorComponent", "processor", "innerRef", "skipCache", "importName"]);
    var _e = getAppData(scope), manifestLocation = _e.manifestLocation, pluginManifest = _e.pluginManifest;
    var _f = useReducer(function (prev) { return prev + 1; }, 0), reRender = _f[0], forceRender = _f[1];
    var _g = useState(undefined), Component = _g[0], setComponent = _g[1];
    var _h = useState(), prefetchPromise = _h[0], setPrefetchPromise = _h[1];
    var _j = useState(), loadingError = _j[0], setLoadingError = _j[1];
    if (loadingError) {
        // Error has to be thrown during render loop. Promise based errors won't be catched by error boundary.
        throw loadingError;
    }
    var scalprumApi = useScalprum();
    useEffect(function () {
        var prefetchID = "".concat(scope, "#").concat(module);
        var _a = getCachedModule(scope, module), cachedModule = _a.cachedModule, prefetchPromise = _a.prefetchPromise;
        setPrefetchPromise(prefetchPromise);
        var isMounted = true;
        var handleLoadingError = function (error) {
            if (isMounted) {
                setLoadingError(error);
                setComponent(function () { return function (props) { return React.createElement(ErrorComponent, __assign({ error: error }, props)); }; });
            }
        };
        var pref;
        /**
         * Check if module is being pre-loaded
         */
        var pendingLoading = getPendingLoading(scope, module);
        if (!cachedModule && pendingLoading) {
            pendingLoading.finally(function () {
                forceRender();
            });
        }
        else {
            /**
             * Here will be registry check
             */
            if (!cachedModule) {
                if (manifestLocation || pluginManifest) {
                    var processPromise = processManifest((manifestLocation !== null && manifestLocation !== void 0 ? manifestLocation : pluginManifest), scope, module, processor)
                        .then(function () {
                        pref = setComponentFromModule(scope, module, isMounted, importName, setComponent);
                        pref.then(function (result) {
                            if (result) {
                                var prefetch = getPendingPrefetch(prefetchID) || result(scalprumApi);
                                setPrefetchPromise(prefetch);
                                handlePrefetchPromise(prefetchID, prefetch);
                            }
                        });
                        return pref;
                    })
                        .catch(handleLoadingError);
                    // lock module preload
                    setPendingLoading(scope, module, processPromise);
                }
            }
            else {
                try {
                    isMounted && setComponent(function () { return cachedModule[importName]; });
                    pref = cachedModule.prefetch;
                    if (pref) {
                        var prefetch = getPendingPrefetch(prefetchID) || pref(scalprumApi);
                        setPrefetchPromise(prefetch);
                        handlePrefetchPromise(prefetchID, prefetch);
                    }
                }
                catch (e) {
                    handleLoadingError(e);
                }
            }
        }
        return function () {
            isMounted = false;
            setLoadingError(undefined);
        };
    }, [scope, skipCache, reRender]);
    // clear prefetchPromise (from factory)
    var initialPrefetch = useRef(false);
    useEffect(function () {
        if (initialPrefetch.current) {
            setPrefetchPromise(undefined);
        }
        initialPrefetch.current = true;
    }, [scope, module]);
    return (React.createElement(PrefetchProvider, { prefetchPromise: prefetchPromise },
        React.createElement(Suspense, { fallback: fallback }, Component ? React.createElement(Component, __assign({ ref: innerRef }, props)) : fallback)));
};
var BaseScalprumComponent = /** @class */ (function (_super) {
    __extends(BaseScalprumComponent, _super);
    function BaseScalprumComponent(props) {
        var _this = _super.call(this, props) || this;
        _this.state = { hasError: false };
        _this.selfRepairAttempt = false;
        return _this;
    }
    BaseScalprumComponent.getDerivedStateFromError = function () {
        return { hasError: true };
    };
    BaseScalprumComponent.prototype.shouldComponentUpdate = function (nextProps, nextState) {
        if (this.state.hasError !== nextState.hasError) {
            return true;
        }
        return !isEqual(nextProps, this.props) || !isEqual(nextState, this.state);
    };
    // TODO: Use ErrorWithCause once the type is available
    BaseScalprumComponent.prototype.componentDidCatch = function (error, errorInfo) {
        if (this.selfRepairAttempt === true) {
            console.error('Scalprum encountered an error!', (error === null || error === void 0 ? void 0 : error.cause) || error.message, error);
            this.setState({ error: error, errorInfo: errorInfo });
        }
        else {
            console.warn('Scalprum failed to render component. Attempting to skip module cache.');
            this.setState({ repairAttempt: true });
        }
    };
    BaseScalprumComponent.prototype.render = function () {
        var _this = this;
        var _a = this.props, _b = _a.ErrorComponent, ErrorComponent = _b === void 0 ? React.createElement(DefaultErrorComponent, __assign({}, this.state)) : _b, props = __rest(_a, ["ErrorComponent"]);
        var PreparedError = function (props) {
            return React.cloneElement(ErrorComponent, __assign(__assign({}, _this.state), props));
        };
        if (this.state.repairAttempt && !this.selfRepairAttempt) {
            /**
             * Retry fetching module with disabled cache
             */
            this.selfRepairAttempt = true;
            return React.createElement(LoadModule, __assign({}, props, { skipCache: true, ErrorComponent: PreparedError }));
        }
        if (this.state.hasError && this.selfRepairAttempt) {
            return React.cloneElement(ErrorComponent, __assign({}, this.state));
        }
        return React.createElement(LoadModule, __assign({}, props, { ErrorComponent: PreparedError }));
    };
    BaseScalprumComponent.defaultProps = {
        ErrorComponent: React.createElement(DefaultErrorComponent, null),
    };
    return BaseScalprumComponent;
}(React.Component));
export var ScalprumComponent = React.forwardRef(function (props, ref) { return (React.createElement(BaseScalprumComponent, __assign({}, props, { innerRef: ref }))); });
//# sourceMappingURL=scalprum-component.js.map